import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/lib/utils";
import { StripePrice } from '@/types';
import { Module, Section } from '@/lib/types';
import StripeCheckout from "./StripeCheckout";
import { useTranslation } from "react-i18next";

interface EnrollmentButtonProps {
  course: {
    is_free: boolean;
    slug?: string;
    title?: string;
    default_price?: StripePrice | null;
    modules?: Module[];
    course_type?: 'multi-lesson' | 'single-page';
    sections?: Section[];
  };
  userId: string | undefined;
  hasAccess: boolean;
  setHasAccess: (hasAccess: boolean) => void;
  lang: string;
  setAuthModalOpen: (isOpen: boolean) => void;
  className?: string;
}

export function EnrollmentButton({
  course,
  userId,
  hasAccess,
  setHasAccess,
  lang,
  setAuthModalOpen,
  className = ""
}: EnrollmentButtonProps) {
  const navigate = useNavigate();
  const [enrolling, setEnrolling] = useState(false);
  const { t } = useTranslation();

  const handleStartCourse = () => {
    if (!course?.slug) {
      toast({
        title: "Error",
        description: "Could not find course slug.",
        variant: "destructive",
      });
      return;
    }

    // Handle single-page courses
    if (course.course_type === 'single-page') {
      const navigationUrl = `/${lang === 'en' ? '' : lang + '/'}courses/${course.slug}`;
      navigate(navigationUrl);
      return;
    }

    // Handle multi-lesson courses
    if (!course.modules || course.modules.length === 0) {
      toast({
        title: "Error",
        description: "Could not find course content structure.",
        variant: "destructive",
      });
      return;
    }

    // Ensure modules are sorted by position to get the correct first module
    const sortedModules = [...course.modules].sort((a, b) => a.position - b.position);
    const firstModule = sortedModules[0];

    if (!firstModule || !firstModule.id || !firstModule.lessons || firstModule.lessons.length === 0) {
      toast({
        title: "Error",
        description: "Could not find the first lesson structure.",
        variant: "destructive",
      });
      return;
    }

    // Ensure lessons are sorted by position to get the correct first lesson
    const sortedLessons = [...firstModule.lessons].sort((a, b) => a.position - b.position);
    const firstLesson = sortedLessons[0];

    if (!firstLesson || !firstLesson.slug) {
      toast({
        title: "Error",
        description: "Could not find the first lesson slug.",
        variant: "destructive",
      });
      return;
    }

    const firstLessonSlug = firstLesson.slug;
    const navigationUrl = `/${lang === 'en' ? '' : lang + '/'}courses/${course.slug}/${firstModule.id}/${firstLessonSlug}`;
    navigate(navigationUrl);
  };

  const handleFreeEnrollment = async () => {
    if (!userId || !course.slug) {
      setAuthModalOpen(true);
      return;
    }

    setEnrolling(true);
    try {
      // Check if user already has access
      const { data: existingAccess } = await supabase
        .from('user_course_access')
        .select('id')
        .eq('user_id', userId)
        .eq('course_slug', course.slug)
        .single();

      if (existingAccess) {
        setHasAccess(true);
        toast({
          title: t("enrollment.already_enrolled"),
          description: t("enrollment.already_enrolled_description", { title: course.title || 'this course' }),
        });
        handleStartCourse();
        return;
      }

      // Insert into user_course_access for immediate access
      const { error: accessError } = await supabase
        .from('user_course_access')
        .insert({
          user_id: userId,
          course_slug: course.slug,
          progress: 0,
          completed_sections: []
        });

      if (accessError) throw accessError;

      // Also insert into enrollments table for record keeping
      const { error: enrollmentError } = await supabase
        .from('enrollments')
        .insert({
          user_id: userId,
          course_slug: course.slug
        });

      if (enrollmentError) {
        console.warn('Enrollment record creation failed, but access was granted:', enrollmentError);
      }

      setHasAccess(true);
      toast({
        title: t("enrollment.enrolled"),
        description: t("enrollment.enrolled_description", { title: course.title || 'this course' }),
      });
      handleStartCourse();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Enrollment failed";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setEnrolling(false);
    }
  };

  // Generate button text based on course type and access status
  const getButtonText = () => {
    if (hasAccess) return t("enrollment.start_learning");
    if (course.is_free) return t("enrollment.enroll_for_free");
    if (course.default_price?.unit_amount) {
      const price = formatPrice(course.default_price.unit_amount, course.default_price.currency, lang === 'fr' ? 'fr-FR' : 'en-US');
      return t("enrollment.enroll_for_price", { price });
    }
    return t("enrollment.enroll_now");
  };

  const buttonClasses = `bg-primary text-primary-foreground px-8 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-70 ${className}`;

  if (hasAccess) {
    return (
      <button
        onClick={handleStartCourse}
        className={buttonClasses}
      >
        {getButtonText()}
      </button>
    );
  }

  if (course.is_free) {
    return (
      <button
        onClick={handleFreeEnrollment}
        disabled={enrolling}
        className={buttonClasses}
      >
        {enrolling ? t("enrollment.enrolling") : getButtonText()}
      </button>
    );
  }

  if (userId) {
    return (
      <div className={className}>
        <StripeCheckout
          courseSlug={course.slug!}
          userId={userId}
          customButtonText={getButtonText()}
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => setAuthModalOpen(true)}
      className={buttonClasses}
    >
      {getButtonText()}
    </button>
  );
} 